/**
 * Sets a CSS variable for the given component.
 * If the current browser supports ShadowDOM, the variable will be appended to the <style> tag of the shadowRoot.
 * If it does not (IE, Edge < 80, unit/snapshot tests), the variable will be set as inline style on the hostElement.
 *
 * @param hostElement The component in whose context the CSS variable should be set
 * @param name The name of the CSS variable, e.g. "--myvar"
 * @param value The value of the CSS variable to set, e.g. "var(--eon-white)"
 */
export function setCssVariable(hostElement: HTMLElement, name: string, value: string): void {
  // at least one of those is false in IE, legacy Edge and jest tests (snapshots)
  const browserSupportsShadowDom = window.ShadowRoot != null;
  const componentHasShadowRoot = hostElement.shadowRoot != null;
  // shadowed component in modern browser (FF, Chrome in dev build mode):
  const componentHasShadowStyle =
    componentHasShadowRoot &&
    hostElement.shadowRoot.styleSheets != null &&
    hostElement.shadowRoot.styleSheets.length > 0;
  // shadowed component in modern browser (Chrome in prod mode - no idea why this is different):
  const componentHasAdoptedShadowStyle =
    componentHasShadowRoot && (hostElement.shadowRoot as any).adoptedStyleSheets != null;

  if (componentHasAdoptedShadowStyle) {
    // Append the single CSS variable declaration to the <style> tag of the shadowRoot but using the adoptedStyleSheets API.
    // Get information whether component is a schemable component.
    // If we would attach new rules to baseStyleSheet, they are applied globally for all components of this type.
    // Hence we have to create a separate Stylesheet for variables.

    // with scheme:
    // adoptedStyleSheets[0]: baseStyleSheet
    // adoptedStyleSheets[1]: schemeStyleSheet
    // adoptedStyleSheets[2]: varStyleSheet

    // without scheme:
    // adoptedStyleSheets[0]: baseStyleSheet
    // adoptedStyleSheets[1]: varStyleSheet

    const baseStyleSheet = hostElement.shadowRoot.adoptedStyleSheets[0];
    let adoptedStyleSheetAmount: number = hostElement.shadowRoot.adoptedStyleSheets.length;
    let variableStyleSheet: any;
    let variableStyleSheetsLength: number;

    switch (adoptedStyleSheetAmount) {
      case 1:
        // only baseStyleSheet available --> create variableStyleSheet
        variableStyleSheet = new CSSStyleSheet();
        variableStyleSheet.insertRule(`:host { ${name}: ${value} }`);
        hostElement.shadowRoot.adoptedStyleSheets = [baseStyleSheet, variableStyleSheet];
        break;
      case 2:
        // baseStyleSheet and variableStyleSheet available --> use variableStyleSheet on index 1
        variableStyleSheet = hostElement.shadowRoot.adoptedStyleSheets[1];
        variableStyleSheetsLength = variableStyleSheet.cssRules.length;
        variableStyleSheet.insertRule(`:host { ${name}: ${value} }`, variableStyleSheetsLength);
        break;
      case 3:
        // baseStyleSheet, schemeStyleSheet and variableStyleSheet available --> use variableStyleSheet on index 2
        variableStyleSheet = hostElement.shadowRoot.adoptedStyleSheets[2];
        variableStyleSheetsLength = variableStyleSheet.cssRules.length;
        variableStyleSheet.insertRule(`:host { ${name}: ${value} }`, variableStyleSheetsLength);
        break;
    }
  } else if (componentHasShadowStyle) {
    // append the CSS variable declaration to the <style> tag of the shadowRoot
    (hostElement.shadowRoot.styleSheets[0] as any).addRule(`:host`, `${name}: ${value}`);
  } else if (!componentHasShadowRoot || !browserSupportsShadowDom) {
    // add the CSS variable declaration as inline style to the hostElement
    hostElement.style.setProperty(`${name}`, `${value}`);
  } // else: component might be shadowed in a modern browser but the stylesheet isn't ready yet. Happens frequently in FF/Chrome.
}

export function createSchemeStyleScheet(activeScheme: any): any {
  const schemeStyleSheet: any = new CSSStyleSheet();
  for (let [key, value] of Object.entries(activeScheme)) {
    // this check allows also setting non color variable values to a component scheme
    if (typeof value === "string" && value.startsWith("eon-")) {
      schemeStyleSheet.insertRule(`:host { --${key}: var(--${value}) }`);
    } else {
      schemeStyleSheet.insertRule(`:host { --${key}: ${value} }`);
    }
  }

  return schemeStyleSheet;
}

/**
 * Returns the value of the given CSS variable defined as inline style on the hostElement (IE/Edge/tests) or within its shadowRoot.
 *
 * @param hostElement The component in whose context the CSS variable is set
 * @param name The name of the CSS variable, e.g. "--myvar"
 */
export function getCssVariable(hostElement: HTMLElement, name: string): string {
  const style = window.getComputedStyle(hostElement);
  if (style != null) return style.getPropertyValue(name);
  return null;
}
